import { Injectable } from '@angular/core';
import {
  AyaPriceListProgram,
  AyaPriceListSchool,
  AyaSchoolsByCountryTable,
  AyaSchoolDataByLanguage,
  AyaSchoolTableColumn,
  AyaSchoolTableRow,
  AyaSchoolTableView,
  DestinationFilter,
} from '../models';
import { CodeAndName } from '@lx/shared/types';

@Injectable({
  providedIn: 'root',
})
export class AyaSchoolService {
  private readonly iconByProgram: Record<string, string> = {
    language_and_culture: 'fa-comments',
    university_preparation: 'fa-graduation-cap',
    professional_preparation: 'fa-briefcase',
  };

  groupSchoolsByLanguageAndCountryToTables(schools: AyaPriceListSchool[]): AyaSchoolDataByLanguage[] {
    if (!schools?.length) {
      return [];
    }

    const groupsByCountry = this.groupByCountry(schools);
    return this.groupByLanguage(groupsByCountry);
  }

  /**
   * Filters the given AyaSchoolsByLanguageTable entries by the selected country and school filters.
   * @param schoolsByLanguage the AyaSchoolsByLanguageTable entries to filter
   * @param selectedFilters the selected country and school filters
   */
  filterSchools(schoolsByLanguage: AyaSchoolDataByLanguage[], selectedFilters: DestinationFilter[]) {
    const countryFilters = selectedFilters.filter(({ data }) => data === 'country');
    const schoolFilters = selectedFilters.filter(({ data }) => data === 'school');

    return (
      schoolsByLanguage
        .map((entry) => this.getFilteredSchools(entry, countryFilters, schoolFilters))
        // remove entries with no matching data
        .filter((schoolsByLanguage) => schoolsByLanguage.schoolsByCountryTable.length)
    );
  }

  private groupByCountry(schools: AyaPriceListSchool[]) {
    const groupsByCountry = schools.reduce((groupsByCode, school) => {
      const country = school.schoolInfo?.country;
      const code = country?.code;

      if (!code) {
        return groupsByCode;
      }

      const mappedSchool = this.mapSchoolToTableView(school);
      const existing = groupsByCode.get(code);

      if (existing) {
        const updated: AyaSchoolsByCountryTable = {
          ...existing,
          schools: [...existing.schools, mappedSchool],
        };
        groupsByCode.set(code, updated);
      } else {
        const created: AyaSchoolsByCountryTable = {
          country: country as CodeAndName,
          schools: [mappedSchool],
        };
        groupsByCode.set(code, created);
      }

      return groupsByCode;
    }, new Map<string, AyaSchoolsByCountryTable>());

    return [...groupsByCountry.values()];
  }

  private groupByLanguage(groupsByCountry: AyaSchoolsByCountryTable[]) {
    const groupedByLanguage = groupsByCountry.reduce((map, item) => {
      // Assume the language is the same for all schools in the country group (business rule)
      const language = item.schools[0]?.schoolLanguage || '';
      if (!map.has(language)) {
        map.set(language, []);
      }

      map.get(language).push(item);

      return map;
    }, new Map<string, AyaSchoolsByCountryTable[]>());

    return [...groupedByLanguage.entries()].map(([language, schools]) => ({
      language,
      schoolsByCountryTable: schools,
    }));
  }

  /**
   * Produces a new AyaSchoolsByLanguageTable by filtering out the schools by the given country and school filters.
   * @param entry the AyaSchoolsByLanguageTable entry to filter
   * @param countryFilters the country filters
   * @param schoolFilters the school filters
   * @private
   */
  private getFilteredSchools(entry: AyaSchoolDataByLanguage, countryFilters: DestinationFilter[], schoolFilters: DestinationFilter[]) {
    const { language, schoolsByCountryTable } = entry;

    // reduce the schools to only those selected by countries and schools filters
    const filtered = schoolsByCountryTable
      .map(({ country, schools }) => {
        // a school will be selected if its country is selected or if the school itself is selected
        const filteredSchools = schools.filter(
          ({ schoolCode }) =>
            (country.code && countryFilters.some(({ key }) => key === country.code)) || (schoolCode && schoolFilters.some(({ key }) => key === schoolCode)),
        );

        return {
          country,
          schools: filteredSchools,
        };
      })
      // remove countries with no matching schools
      .filter(({ schools }) => schools.length);

    return {
      language,
      schoolsByCountryTable: filtered,
    };
  }

  /**
   * Map ONE school DTO to a PrimeNG-table-friendly view.
   * - dynamic month columns
   * - one row per course
   * - includes "Additional Information"
   */
  private mapSchoolToTableView(school: AyaPriceListSchool): AyaSchoolTableView {
    const { schoolInfo, programs, additionalInformation, programDates, sessionBreaks, supplements } = school || {};
    const currency = schoolInfo?.currency?.name ?? '';

    const durations = this.collectDurations(programs);
    const columns = this.buildColumns(durations);
    const rows = this.buildRows(programs, durations);
    const { code: schoolCode, name: schoolName } = schoolInfo?.school || {};

    return {
      schoolCode,
      schoolName,
      schoolLanguage: schoolInfo?.language,
      currency,
      columns,
      rows,
      additionalInformation,
      programDates,
      sessionBreaks,
      supplements,
    };
  }

  private collectDurations(programs: ReadonlyArray<AyaPriceListProgram>): number[] {
    return [
      ...new Set(
        programs
          .flatMap((program) => program.courses ?? [])
          .flatMap((course) => course.prices ?? [])
          .map((price) => price?.duration),
      ),
    ];
  }

  private buildColumns(durations: number[]): AyaSchoolTableColumn[] {
    return durations.map((duration) => ({
      field: `m${duration}`,
      duration,
      header: `${duration} months`,
    }));
  }

  private buildRows(programs: AyaPriceListProgram[] = [], durations: number[]): AyaSchoolTableRow[] {
    return programs.flatMap(({ code: programCode, name: programName, courses = [] }) => {
      // Group courses by identical price signature across all durations
      const courseGroupsByPriceSignature = courses.reduce((courseGroupsMap, { name: courseName, prices = [] }) => {
        // Build a record of month-based price cells for this course
        const monthPriceCells = durations.reduce<Record<string, number | undefined>>((durationPriceMap, duration) => {
          durationPriceMap[`m${duration}`] = prices.find((price) => price.duration === duration)?.value;
          return durationPriceMap;
        }, {});

        // Create a unique signature string for these prices
        const priceSignature = durations.map((duration) => monthPriceCells[`m${duration}`] ?? '').join('|');

        // Group courses with the same signature
        const existingGroup = courseGroupsMap.get(priceSignature);
        if (existingGroup) {
          existingGroup.courseNames.push(courseName);
        } else {
          courseGroupsMap.set(priceSignature, {
            courseNames: [courseName],
            monthCells: monthPriceCells,
          });
        }

        return courseGroupsMap;
      }, new Map<string, { courseNames: string[]; monthCells: Record<string, number | undefined> }>());

      const groupedCourses = [...courseGroupsByPriceSignature.values()];
      const programRowSpanCount = groupedCourses.length;

      return groupedCourses.map(({ courseNames, monthCells }, indexInProgram) => ({
        programCode,
        programClass: programCode.replace(/_/g, '-').toLowerCase(),
        programIcon: this.iconByProgram[programCode],
        programName,
        courseNames,
        firstOfProgram: indexInProgram === 0,
        programRowspan: indexInProgram === 0 ? programRowSpanCount : 0,
        ...monthCells,
      }));
    });
  }
}
